import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";

import ReactEcharts from "echarts-for-react";
import { format, addDays, addMonths, isSameMonth } from "date-fns";

import axios from "@app/lib/axios";

import CommonHorizontalBarChart from "@app/components/CommonHorizontalBarChart";
import CommonVerticalBarChart from "@app/components/CommonVerticalBarChart";

import style from "../Components/style";

const appConnectChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: style.seriesAppConnect,
    },
    stacks: [{ stack: "base", showPercent: true, showSum: false, showSumTooltip: true }],
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    showSum: true,
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%",
    },
};
const appNewChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: style.seriesAppNewUser,
    },
    stacks: [{ stack: "base", showPercent: false, showSum: false, showSumTooltip: false }],
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%",
    },
};
const appAgeChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: style.seriesAge,
    },
    stacks: [
        { stack: "base3", showSum: false },
        { stack: "base4", showSum: false },
        { stack: "base5", showSum: false },
        { stack: "base345", showSum: false },
    ],
    tooltipalign: "vertical",
    tooltipFormatter: (params, chart, test) => {
        var tooltipHtml = [""];
        var tooltipTitle = "";
        var total = 0;

        params.map((e) => {
            if (e.seriesName.indexOf("Sum-") < 0) {
                total += e.value;
            }
        });
        params.map((e) => {
            if (e.seriesName.indexOf("Sum-") < 0) {
                if (!tooltipTitle) tooltipTitle += e.name + `<br/>`;

                const stackIndex = 0;
                tooltipHtml[stackIndex] += e.marker + e.seriesName + " : " + (e.value ? e.value.toLocaleString() : "-");
                tooltipHtml[stackIndex] += total > 0 ? ` (` + Math.round((e.value / total) * 100) + "%)" : "";
                tooltipHtml[stackIndex] += "<br/>";
            }
        });
        var tooltip = tooltipTitle + '<div class="text-left">';
        tooltipHtml.forEach((t, index) => {
            tooltip += t;
        });
        tooltip += `<b>합계: ${total.toLocaleString()}</b>`;
        tooltip += `</div>`;

        return tooltip;
    },
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%",
    },
};
const versionChartOption = {
    xAxis: {
        name: "건",
        series: style.seriesAppVersion,
    },
    yAxis: { field: "version" },
    stacks: [{ stack: "base", showPercent: false, showSum: false, showSumTooltip: false }],
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%",
    },
};
const contentPlayCountChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: [style.seriesAppContent[0]],
    },
    stacks: [{ stack: "base", showSum: false, showSumTooltip: false }],
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "10%",
        left: "5%",
        right: "5%",
    },
};
const contentPlayCountSumChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: [style.seriesAppContent[1]],
    },
    stacks: [{ stack: "base", showSum: false, showSumTooltip: false }],
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%",
    },
};
const menuChartOption = {
    xAxis: {
        name: "건",
        series: style.seriesAppMenu,
    },
    yAxis: { field: "menu" },
    stacks: [{ stack: "base", showPercent: false, showSum: false, showSumTooltip: false }],
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "30px",
        left: "10%",
        right: "5%",
    },
};
const App = forwardRef(({ statPeriod }, ref) => {
    const [statApp, setStatApp] = useState(false);
    const [statAppAge, setStatAppAge] = useState(false);
    const [statContent, setStatContent] = useState(false);
    const [statVersion, setStatVersion] = useState(false);
    const [statMenu, setStatMenu] = useState(false);

    const initStat = () => {
        setStatApp(false);
        setStatAppAge(false);
        setStatContent(false);
        setStatVersion(false);
        setStatMenu(false);
    };

    useImperativeHandle(ref, () => ({
        getExcelData(callback) {
            const excelData = {
                fileName: "토모노트_앱통계",
                sheets: [
                    { name: "접속", title: "접속 수 통계", columnTitle: ["일자", "접속 수"], data: statApp ? statApp.map((s) => [s.statDate, s.connectUser || 0]) : [] },
                    { name: "앱최초실행", title: "앱 최초 실행 수 통계", columnTitle: ["일자", "실행 수"], data: statApp ? statApp.map((s) => [s.statDate, s.newUser || 0]) : [] },
                    {
                        name: "연령",
                        title: "연령별 사용량 통계",
                        columnTitle: ["일자", "만3세", "만4세", "만5세", "혼합"],
                        data: statAppAge ? statAppAge.map((s) => [s.statDate, s.playCountSum3 || 0, s.playCountSum4 || 0, s.playCountSum5 || 0, s.playCountSum345 || 0]) : [],
                    },
                    {
                        name: "콘텐츠",
                        title: "콘텐츠 실행 통계",
                        columnTitle: ["일자", "순 실행 수", "누적 실행 수"],
                        data: statContent ? statContent.map((s) => [s.statDate, s.playCount || 0, s.playCountSum || 0]) : [],
                    },
                    {
                        name: "메뉴",
                        title: "메뉴별 사용 통계",
                        columnTitle: ["메뉴", "메뉴 이용량"],
                        data: statMenu ? statMenu.map((s) => [s.menu, s.playCountSum || 0]) : [],
                    },
                ],
            };
            callback(null, excelData);
        },
    }));
    const getStat = useCallback(() => {
        initStat();
        axios({
            method: "POST",
            url: `/api/stat_note/hq/apps`,
            data: {
                startDate: statPeriod.startDate,
                endDate: statPeriod.endDate,
                periodType: statPeriod.type,
            },
        })
            .then((result) => {
                if (result.data.code === 200) {
                    const stat = result.data;
                    let statApp = [];
                    let statAppAge = [];
                    let statContent = [];

                    let start = new Date(statPeriod.startDate);
                    let day = new Date(statPeriod.startDate);
                    for (let i = 0; i < 99; i++) {
                        const statDate = format(day, statPeriod.type === "year" ? "yyyy-MM" : "yyyy-MM-dd");
                        const statDateStr = format(day, statPeriod.type === "year" ? "yyyy-MM" : "MM-dd");
                        const sApp = stat.apps.find((s) => s.statDate === statDate);
                        const sAges = stat.ages.filter((s) => s.statDate === statDate);
                        const sContent = stat.contents.find((s) => s.statDate === statDate);
                        const sAge = {};
                        sAges.forEach((a) => {
                            sAge[`playCountSum${a.age}`] = a.playCountSum;
                        });

                        statApp.push({ ...sApp, statDate: statDateStr });
                        statAppAge.push({ ...sAge, statDate: statDateStr });
                        statContent.push({ ...sContent, statDate: statDateStr });

                        if (statPeriod.type === "week") {
                            day = addDays(day, 1);
                            if (i >= 6) break;
                        } else if (statPeriod.type === "month") {
                            day = addDays(day, 1);
                            if (!isSameMonth(day, start)) break;
                        } else {
                            // year
                            day = addMonths(day, 1);
                            if (i > 11) break;
                        }
                    }
                    stat.menus.sort((a, b) => b.playCountSum - a.playCountSum);
                    setStatApp(statApp);
                    setStatAppAge(statAppAge);
                    setStatContent(statContent);
                    setStatVersion(stat.versions);
                    setStatMenu(stat.menus);
                } else {
                    alert(result.data && result.data.msg ? result.data.msg : "통계 정보를 가져오는데 실패하였습니다.\n지속 발생시 관리자에게 문의해주세요.");
                }
            })
            .catch((error) => {
                console.log(error);
                console.error("알 수 없는 오류가 발생하였습니다. \n지속 발생시 관리자에게 문의해주세요.");
            });
    }, [statPeriod]);
    useEffect(() => {
        getStat();
        return () => {};
    }, [getStat]);

    const heightChartMenu = statMenu && statMenu.length > 10 ? (statMenu.length > 100 ? statMenu.length * 5 + 25 : statMenu.length * 15 + 25) : 250;
    return (
        <div className="w-100">
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">접속 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statApp} chartOption={appConnectChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">앱 최초 실행 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statApp} chartOption={appNewChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">연령별 사용량</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statAppAge} chartOption={appAgeChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">앱 버전별 사용 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonVerticalBarChart chartData={statVersion} chartOption={versionChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">콘텐츠 순 실행 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statContent} chartOption={contentPlayCountChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">콘텐츠 누적 실행 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statContent} chartOption={contentPlayCountSumChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">메뉴별 사용량 - {statMenu.length}</h4>
                        </div>
                        <div className="card-body" style={{ height: `${heightChartMenu}px` }}>
                            <CommonVerticalBarChart chartData={statMenu} chartOption={menuChartOption} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default App;
